import { Paper, Skeleton } from '@mui/material'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ChangeEvent, CSSProperties, MouseEvent, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { Pagination } from '../../types/interfaces'
import { TablePaginationActions } from './TablePaginationActions'

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  containerStyle?: CSSProperties
  currentPage: number
  data: TData[]
  isLoading?: boolean
  onChangePagination: (pagination: Pagination) => void
  pageSize?: number
  totalItems: number
}

export function RidesTable<TData, TValue>({
  columns,
  containerStyle,
  currentPage,
  data,
  isLoading,
  onChangePagination,
  pageSize = 10,
  totalItems,
}: DataTableProps<TData, TValue>) {
  const { t } = useTranslation()

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const handleChangeRowsPerPage = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const size = event.target.value ? Number(event.target.value) : 10
      table.setPageSize(size)

      onChangePagination({
        page: 1,
        pageSize: size,
      })
    },
    [onChangePagination, table]
  )

  const handleChangePage = useCallback(
    (_: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      table.setPageIndex(newPage)

      onChangePagination({
        page: Math.max(1, newPage),
        pageSize,
      })
    },
    [table, onChangePagination, pageSize]
  )

  const getTableBody = useCallback(() => {
    if (isLoading) {
      return Array.from({ length: 3 }).map((_, index) => (
        <TableRow key={index}>
          {columns.map((column, colIndex) => (
            <TableCell key={colIndex}>
              <Skeleton
                animation="wave"
                height={20}
                variant="rectangular"
                width={120}
              />
            </TableCell>
          ))}
        </TableRow>
      ))
    }

    return table.getRowModel().rows?.length ? (
      table.getRowModel().rows.map((row) => (
        <TableRow key={row.id}>
          {row.getVisibleCells().map((cell) => (
            <TableCell key={cell.id}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
          ))}
        </TableRow>
      ))
    ) : (
      <TableRow>
        <TableCell colSpan={columns.length}>No results.</TableCell>
      </TableRow>
    )
  }, [table, columns, isLoading])

  return (
    <Paper>
      <TableContainer sx={containerStyle}>
        <Table stickyHeader>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>{getTableBody()}</TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        ActionsComponent={TablePaginationActions}
        component="div"
        count={totalItems}
        labelRowsPerPage={t('earnings.rows_per_page')}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        page={currentPage - 1}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[5, 10, 25]}
        slotProps={{
          select: {
            inputProps: { 'aria-label': 'rows per page' },
          },
        }}
      />
    </Paper>
  )
}
